import {useCallback, useEffect, useState} from "react";
import {useParams} from "react-router-dom";
import {z} from "zod";
import {SubmitHandler, useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {Separator} from "@/components/ui/separator.tsx";
import {
    Form,
    FormControl, FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@/components/ui/select.tsx";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {queryKeys} from "@/services/queryKeyFactory.ts";
import {getItemsOfAllTypes} from "@/services/itemService.ts";
import {RadioGroup, RadioGroupItem} from "@/components/ui/radio-group.tsx";
import {AllCriteria, Criteria} from "@/types/Criteria.ts";
import {useToast} from "@/components/ui/use-toast.ts";
import {Button} from "@/components/ui/button.tsx";
import {Checkbox} from "@/components/ui/checkbox.tsx";
import {CreatePreferenceDto, Preference} from "@/types/Preference.ts";
import {
    createPreference,
    getPreferenceByMenteeId,
    updatePreference,
    deletePreference
} from "@/services/preferenceService.ts";
import {PreferenceDetails} from "@/components/PreferenceDetails.tsx";
import {Loader2} from "lucide-react";
import {fetchLastDateToSubmitPreferences} from "@/services/settingsService.ts";
import {getEffectiveDeadlines} from "@/services/deadlineExtensionService.ts";
import {getAffinityGroups} from "@/services/affinityGroupService.ts";
import {Card, CardTitle, CardDescription, CardHeader, CardContent} from "@/components/ui/card.tsx";
import {PageTitle, SectionTitle} from "@/components/ui/headings";
import {keyValueMapping} from "@/utils/criteria-helper.tsx";
import {Trans, useTranslation} from "react-i18next";
import {getLocalizedFieldName} from "@/utils/translation-helper.ts";
import {isPreferenceSubmissionPeriod} from "@/utils/time-helper.ts";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog.tsx";

interface Criterion {
    title: string;
    description: string;
}

const criteriaData: Criterion[] = [
    {
        title: 'criteria.careerFocus',
        description: 'preference.careerFocusDesc',
    },
    {
        title: 'criteria.experience',
        description: 'preference.experienceDesc',
    },
    {
        title: 'criteria.discipline',
        description: 'preference.disciplineDesc',
    },
    {
        title: 'criteria.researchExpertise',
        description: 'preference.researchExpertiseDesc',
    },
    {
        title: 'criteria.socioeconomicDisadvantages',
        description: 'preference.socioeconomicDisadvantagesDesc',
    },
    {
        title: 'criteria.races',
        description: 'preference.racesDesc',
    },
    {
        title: 'criteria.indigeneities',
        description: 'preference.indigeneitiesDesc',
    },
    {
        title: 'criteria.disabilities',
        description: 'preference.disabilitiesDesc',
    },
    {
        title: 'criteria.gender',
        description: 'preference.genderDesc',
    },
    {
        title: 'criteria.sexualOrientation',
        description: 'preference.sexualOrientationDesc',
    },
];

const PreferenceForm = () => {
    const {t} = useTranslation();
    const {t: vt} = useTranslation("validation");
    const {id} = useParams();
    const [isEditing, setIsEditing] = useState(false);
    const [narrows, setNarrows] = useState<string[]>([]);
    const [preferenceToDelete, setPreferenceToDelete] = useState<Preference | null>(null);

    const queryClient = useQueryClient();
    const {toast} = useToast();

    const {data: globalDeadline, isFetching: fetchingGlobalDeadline} = useQuery({
        queryKey: queryKeys.fetchSettings.lastDateToSubmitPreferences,
        queryFn: () => fetchLastDateToSubmitPreferences(),
        initialData: new Date(new Date().setFullYear(new Date().getFullYear() + 1))
    });

    const {data: effectiveDeadlines} = useQuery({
        queryKey: queryKeys.effectiveDeadlines,
        queryFn: getEffectiveDeadlines,
    });

    const submitPreferenceDeadline = effectiveDeadlines?.preferenceSubmission
        ? new Date(effectiveDeadlines.preferenceSubmission)
        : globalDeadline;
    const fetchingDeadline = fetchingGlobalDeadline;

    const {data: preference, isFetching: fetchingPreference} = useQuery({
        queryKey: queryKeys.fetchPreferences.one,
        queryFn: () => getPreferenceByMenteeId(id ? parseInt(id) : -1),
    });

    const {data: criteria, isFetching: fetchingCriteria} = useQuery({
        queryKey: queryKeys.fetchCriteria.all,
        queryFn: () => getItemsOfAllTypes(),
    });

    const {data: affinityGroups = []} = useQuery({
        queryKey: ["affinityGroups"],
        queryFn: () => getAffinityGroups(),
    });

    const preferenceFormSchema = z.object({
        languageId: z
            .number({
                required_error: vt("selectLanguage"),
            })
            .nullable(),
        wantsAffinityGroup: z.boolean().optional(),
        affinityGroupIds: z.array(z.number()).optional(),
        b1Key: z
            .string({
                required_error: vt("selectBroad1"),
            }),
        b1Value: z
            .string({
                required_error: vt("selectValue"),
                invalid_type_error: vt("selectValue"),
            }),
        b2Key: z
            .string({
                required_error: vt("selectBroad2"),
            }),
        b2Value: z
            .string({
                required_error: vt("selectValue"),
                invalid_type_error: vt("selectValue"),
            }),
        b3Key: z
            .string({
                required_error: vt("selectBroad3"),
            }),
        b3Value: z
            .string({
                required_error: vt("selectValue"),
                invalid_type_error: vt("selectValue"),
            }),
        s1Key: z.string().nullable(),
        s1Value: z.number().nullable(),
        s2Key: z.string().nullable(),
        s2Value: z.number().nullable(),
        s3Key: z.string().nullable(),
        s3Value: z.number().nullable(),
        s4Key: z.string().nullable(),
        s4Value: z.number().nullable(),
    }).superRefine((data, ctx) => {
        if (data.wantsAffinityGroup && (!data.affinityGroupIds || data.affinityGroupIds.length === 0)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: vt("selectAtLeastOne", {fieldName: t("affinity.group")}),
                path: ["affinityGroupIds"],
            });
        }
        if (data.wantsAffinityGroup && data.affinityGroupIds && data.affinityGroupIds.length > 3) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: t("affinity.maxThreeGroups"),
                path: ["affinityGroupIds"],
            });
        }
    });

    type PreferenceFormValues = z.infer<typeof preferenceFormSchema>;

    /** Empty form state used after delete and for manual reset. Needed because form.reset() with no args resets to the last default values (the deleted preference) after query invalidation. */
    const emptyFormValues: PreferenceFormValues = {
        languageId: null,
        wantsAffinityGroup: false,
        affinityGroupIds: [],
        b1Key: "",
        b1Value: "",
        b2Key: "",
        b2Value: "",
        b3Key: "",
        b3Value: "",
        s1Key: null,
        s1Value: null,
        s2Key: null,
        s2Value: null,
        s3Key: null,
        s3Value: null,
        s4Key: null,
        s4Value: null,
    };

    const form = useForm<PreferenceFormValues>({
        resolver: zodResolver(preferenceFormSchema),
        mode: "onChange"
    });

    const b1Key = form.watch("b1Key");
    const b2Key = form.watch("b2Key");
    const b3Key = form.watch("b3Key");
    const b1Value = form.watch("b1Value");
    const b2Value = form.watch("b2Value");
    const b3Value = form.watch("b3Value");
    const wantsAffinityGroup = form.watch("wantsAffinityGroup");

    useEffect(() => {
        const keysAndValues = {
            b1: {key: b1Key, value: b1Value},
            b2: {key: b2Key, value: b2Value},
            b3: {key: b3Key, value: b3Value}
        };
        const newNarrows = [];

        for (const [, {key, value}] of Object.entries(keysAndValues)) {
            if (value === "true" && key && key in keyValueMapping) {
                const {hasNarrow, narrowKeys} = keyValueMapping[key];
                if (hasNarrow && narrowKeys && narrowKeys.length > 0) {
                    newNarrows.push(...narrowKeys);
                }
            }
        }

        setNarrows(newNarrows);

        const keys = form.getValues(["s1Key", "s2Key", "s3Key", "s4Key"]);
        const values = form.getValues(["s1Value", "s2Value", "s3Value", "s4Value"]);

        const keyValueMap = keys.reduce((acc, key, index) => {
            if (key != null) acc[key] = values[index];
            return acc;
        }, {} as { [key: string]: number | null });

        for (let i = 0; i < 4; i++) {
            if (i < newNarrows.length) {
                const narrow = newNarrows[i];
                form.setValue(`s${i + 1}Key` as keyof PreferenceFormValues, narrow);
                form.setValue(`s${i + 1}Value` as keyof PreferenceFormValues, keyValueMap[narrow] ?? null);
            } else {
                form.setValue(`s${i + 1}Key` as keyof PreferenceFormValues, null);
                form.setValue(`s${i + 1}Value` as keyof PreferenceFormValues, null);
            }
        }
    }, [b1Key, b2Key, b3Key, b1Value, b2Value, b3Value, form]);

    useEffect(() => {
        if (preference) {
            form.reset({
                languageId: preference.languageId ?? -1,
                wantsAffinityGroup: (preference.affinityGroups ?? []).length > 0,
                affinityGroupIds: (preference.affinityGroups ?? []).map((item) => item.affinityGroup.id),
                b1Key: preference.b1Key,
                b1Value: preference.b1Value,
                b2Key: preference.b2Key,
                b2Value: preference.b2Value,
                b3Key: preference.b3Key,
                b3Value: preference.b3Value,
                s1Key: preference.s1Key ?? null,
                s1Value: preference.s1Value ?? null,
                s2Key: preference.s2Key ?? null,
                s2Value: preference.s2Value ?? null,
                s3Key: preference.s3Key ?? null,
                s3Value: preference.s3Value ?? null,
                s4Key: preference.s4Key ?? null,
                s4Value: preference.s4Value ?? null,
            });
        }
    }, [preference]);

    const onRequestError = () => {
        toast({
            variant: 'destructive',
            title: t("common.errorTitle"),
            description: t("common.errorDesc"),
        });
    };

    const onCreateSuccess = async () => {
        await queryClient.invalidateQueries({queryKey: queryKeys.fetchPreferences.one});
        setIsEditing(false);
        toast({
            description: t("preference.createSuccess"),
        });
    };

    const createMutation = useMutation({
        mutationFn: createPreference,
        onSuccess: onCreateSuccess,
        onError: onRequestError,
    });

    const onUpdateSuccess = async () => {
        await queryClient.invalidateQueries({queryKey: queryKeys.fetchPreferences.one});
        setIsEditing(false);
        toast({
            description: t("preference.updateSuccess"),
        });
    };

    const updateMutation = useMutation({
        mutationFn: ({id, ...dto}: CreatePreferenceDto & { id: number }) => updatePreference(id, dto),
        onSuccess: onUpdateSuccess,
        onError: onRequestError,
    });

    const deleteMutation = useMutation({
        mutationFn: deletePreference,
        onSuccess: async () => {
            form.reset(emptyFormValues);
            await queryClient.invalidateQueries({queryKey: queryKeys.fetchPreferences.one});
        },
    });

    const onDeleteRequest = useCallback((preference: Preference) => {
        setPreferenceToDelete(preference);
    }, []);

    const onDeleteConfirm = useCallback(() => {
        if (!preferenceToDelete) return;
        deleteMutation.mutate(preferenceToDelete.id, {
            onSuccess: () => {
                setPreferenceToDelete(null);
                toast({description: t("preference.deleteSuccess")});
            },
            onError: onRequestError,
        });
    }, [preferenceToDelete, t]);

    const handleReset = () => {
        form.reset({ ...emptyFormValues, languageId: -1 });
    }

    const onSubmit: SubmitHandler<z.infer<typeof preferenceFormSchema>> = (values: z.infer<typeof preferenceFormSchema>) => {
        const createDto: CreatePreferenceDto = {
            menteeId: preference ? preference.menteeId : (id ? parseInt(id) : -1),
            languageId: !values.languageId || values.languageId === -1 ? null : values.languageId,
            affinityGroupIds: values.wantsAffinityGroup ? (values.affinityGroupIds ?? []) : [],
            b1Key: values.b1Key,
            b1Value: values.b1Value,
            b2Key: values.b2Key,
            b2Value: values.b2Value,
            b3Key: values.b3Key,
            b3Value: values.b3Value,
            s1Key: values.s1Key,
            s1Value: values.s1Value,
            s2Key: values.s2Key,
            s2Value: values.s2Value,
            s3Key: values.s3Key,
            s3Value: values.s3Value,
            s4Key: values.s4Key,
            s4Value: values.s4Value
        };

        if (!preference) {
            createMutation.mutate(createDto);
        } else {
            updateMutation.mutate({...createDto, id: preference.id});
        }
    };

    if (fetchingDeadline || fetchingCriteria || fetchingPreference) {
        return (
            <div className="flex items-center justify-center min-h-screen p-5 min-w-screen">
                <div className="flex space-x-2 animate-pulse">
                    <div className="w-3 h-3 bg-text-3 rounded-full"></div>
                    <div className="w-3 h-3 bg-text-3 rounded-full"></div>
                    <div className="w-3 h-3 bg-text-3 rounded-full"></div>
                </div>
            </div>
        );
    }

    const renderPreferenceDetails = (preference: Preference) => (
        <>
            <div className="container max-w-4xl pb-12 pt-8">
                <PreferenceDetails
                    criteria={criteria}
                    deadline={submitPreferenceDeadline}
                    preference={preference}
                    onEdit={() => setIsEditing(true)}
                    onDelete={onDeleteRequest}
                    showActions={isPreferenceSubmissionPeriod(submitPreferenceDeadline)}
                />
            </div>
            <AlertDialog open={!!preferenceToDelete} onOpenChange={(open) => !open && setPreferenceToDelete(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t("common.areYouSure")}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t("preference.deleteConfirmDesc")}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={deleteMutation.isPending}>{t("common.cancel")}</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => {
                                e.preventDefault();
                                onDeleteConfirm();
                            }}
                            disabled={deleteMutation.isPending}
                            className="bg-status-danger text-white hover:opacity-95"
                        >
                            {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin"/>}
                            {t("common.delete")}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );

    if (isPreferenceSubmissionPeriod(submitPreferenceDeadline)) {
        return preference && !isEditing ? renderPreferenceDetails(preference) : (
            <>
                <div className="container max-w-4xl space-y-8 pb-12 pt-8">
                    <div className="space-y-3">
                        <PageTitle>{t("preference.formTitle")}</PageTitle>
                        <p className="text-base text-text-2">
                            {t("preference.formDesc")}
                        </p>
                    </div>
                    <Separator className="bg-border"/>
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                                <div className="space-y-2">
                                    <SectionTitle>
                                        {t("common.stepX", {step: 1})}: {t("criteria.language")}
                                    </SectionTitle>
                                </div>
                                <div className="rounded-xl border border-border bg-bg-card p-6 space-y-4">
                                    <FormField
                                        control={form.control}
                                        name="languageId"
                                        render={({field}) => (
                                            <FormItem>
                                                <FormLabel className="text-base font-semibold text-text-1">
                                                    {t("criteria.language")}
                                                </FormLabel>
                                                <Select
                                                    onValueChange={value => field.onChange(parseInt(value))}
                                                    value={field.value ? field.value.toString() : undefined}
                                                >
                                                    <FormControl>
                                                        <SelectTrigger className="h-10 bg-bg-card border-border">
                                                            <SelectValue
                                                                placeholder={t("preference.selectLanguagePlaceholder")}/>
                                                        </SelectTrigger>
                                                    </FormControl>
                                                    <SelectContent>
                                                        {criteria?.Language.map((language) => (
                                                            <SelectItem key={language.id} value={language.id.toString()}>
                                                                {language[getLocalizedFieldName("name") as keyof Criteria]}
                                                            </SelectItem>
                                                        ))}
                                                        <SelectItem key="language.none" value="-1">
                                                            {t("preference.noPreference")}
                                                        </SelectItem>
                                                    </SelectContent>
                                                </Select>
                                                <FormDescription className="text-text-3">
                                                    {t("preference.languageFieldDesc")}
                                                </FormDescription>
                                                <FormMessage/>
                                            </FormItem>
                                        )}
                                    />
                                </div>
                                <Separator className="bg-border"/>

                                <div className="space-y-2">
                                    <SectionTitle>
                                        {t("common.stepX", {step: 2})}: {t("preference.pickThreeBroad")}
                                    </SectionTitle>
                                    <p className="text-sm text-text-2">
                                        {t("preference.pickThreeBroadDesc")}
                                    </p>
                                </div>

                                <Card className="border border-border bg-bg-card rounded-xl">
                                    <CardHeader className="pb-3">
                                        <CardTitle className="text-lg font-semibold text-text-1">{t("preference.criteriaOverview")}</CardTitle>
                                        <CardDescription className="text-text-2">
                                            {t("preference.criteriaOverviewDesc")}
                                        </CardDescription>
                                    </CardHeader>
                                    <CardContent className="grid gap-2">
                                        {criteriaData.map((criterion, index) => (
                                            <div key={index}
                                                className="flex space-x-4 rounded-md p-3 transition-colors hover:bg-bg-elevated/60">
                                                <div className="text-text-2 font-medium">{index + 1}</div>
                                                <div className="space-y-1 flex-1">
                                                    <p className="text-sm font-medium leading-none text-text-1">{t(`${criterion.title}`)}</p>
                                                    <p className="text-sm text-text-3">{t(`${criterion.description}`)}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </CardContent>
                                </Card>

                                <div className="rounded-xl border border-border bg-bg-card p-6 space-y-4">
                                    <FormField
                                        control={form.control}
                                        name="b1Key"
                                        render={({field}) => (
                                            <FormItem>
                                                <FormLabel className="text-base font-semibold text-text-1">{t("preference.matchCriteriaX", {idx: "1"})}</FormLabel>
                                                <Select
                                                    onValueChange={value => {
                                                        field.onChange(value);
                                                        // @ts-ignore
                                                        form.setValue("b1Value", null);
                                                    }}
                                                    value={field.value}
                                                >
                                                    <FormControl>
                                                        <SelectTrigger className="h-10 bg-bg-card border-border">
                                                            <SelectValue
                                                                placeholder={t("preference.matchCriteriaPlaceholder")}/>
                                                        </SelectTrigger>
                                                    </FormControl>
                                                    <SelectContent>
                                                        {Object.entries(keyValueMapping)
                                                            .filter(([, value]) => value.broad)
                                                            .map(([key, value]) => {
                                                                const isAlreadySelected = b2Key === key || b3Key === key;
                                                                return (
                                                                    <SelectItem
                                                                        key={`${key}-${value.key}`}
                                                                        value={key}
                                                                        disabled={isAlreadySelected}
                                                                        className={isAlreadySelected ? "text-text-3" : ""}
                                                                    >
                                                                        {t(`criteria.${value.label}`)}
                                                                    </SelectItem>
                                                                );
                                                            })}
                                                    </SelectContent>
                                                </Select>
                                                <FormDescription className="text-text-3">
                                                    {t("preference.broad1Desc")}
                                                </FormDescription>
                                                <FormMessage/>
                                            </FormItem>
                                        )}
                                    />

                                    {criteria && b1Key && (
                                        <FormField
                                            control={form.control}
                                            name="b1Value"
                                            render={({field}) => {
                                                const keyMapping = keyValueMapping[b1Key];
                                                const items = keyMapping.key ? criteria[keyMapping.key as keyof AllCriteria]
                                                    : keyMapping?.bool
                                                        ? [true, false]
                                                        : []
                                                return (
                                                    <FormItem>
                                                        <FormLabel className="text-base font-semibold text-text-1">{t(`criteria.${keyMapping.label}`)}</FormLabel>
                                                        <FormControl>
                                                            <RadioGroup
                                                                onValueChange={field.onChange}
                                                                value={field.value}
                                                                className="flex flex-col space-y-2"
                                                            >
                                                                {items.map((item: any, index: number) => (
                                                                    <FormItem key={index}
                                                                              className="flex items-center space-x-3 space-y-0">
                                                                        <FormControl>
                                                                            <RadioGroupItem
                                                                                value={typeof item === 'boolean' ? item.toString() : item.id.toString()}/>
                                                                        </FormControl>
                                                                        <FormLabel className="font-normal text-text-1 cursor-pointer">
                                                                            {typeof item === 'boolean' ? (item ? t("common.yes") : t("common.no")) : item[getLocalizedFieldName("name")]}
                                                                        </FormLabel>
                                                                    </FormItem>
                                                                ))}
                                                            </RadioGroup>
                                                        </FormControl>
                                                        <FormMessage/>
                                                    </FormItem>
                                                )
                                            }}
                                        />
                                    )}
                                </div>

                                <div className="rounded-xl border border-border bg-bg-card p-6 space-y-4">
                                    <FormField
                                        control={form.control}
                                        name="b2Key"
                                        render={({field}) => (
                                            <FormItem>
                                                <FormLabel className="text-base font-semibold text-text-1">{t("preference.matchCriteriaX", {idx: "2"})}</FormLabel>
                                                <Select
                                                    onValueChange={value => {
                                                        field.onChange(value);
                                                        // @ts-ignore
                                                        form.setValue("b2Value", null);
                                                    }}
                                                    value={field.value}
                                                >
                                                    <FormControl>
                                                        <SelectTrigger className="h-10 bg-bg-card border-border">
                                                            <SelectValue
                                                                placeholder={t("preference.matchCriteriaPlaceholder")}/>
                                                        </SelectTrigger>
                                                    </FormControl>
                                                    <SelectContent>
                                                        {Object.entries(keyValueMapping)
                                                            .filter(([, value]) => value.broad)
                                                            .map(([key, value]) => {
                                                                const isAlreadySelected = b1Key === key || b3Key === key;
                                                                return (
                                                                    <SelectItem
                                                                        key={`${key}-${value.key}`}
                                                                        value={key}
                                                                        disabled={isAlreadySelected}
                                                                        className={isAlreadySelected ? "text-text-3" : ""}
                                                                    >
                                                                        {t(`criteria.${value.label}`)}
                                                                    </SelectItem>
                                                                );
                                                            })}
                                                    </SelectContent>
                                                </Select>
                                                <FormDescription className="text-text-3">
                                                    {t("preference.broadXDesc")}
                                                </FormDescription>
                                                <FormMessage/>
                                            </FormItem>
                                        )}
                                    />

                                    {criteria && b2Key && (
                                        <FormField
                                            control={form.control}
                                            name="b2Value"
                                            render={({field}) => {
                                                const keyMapping = keyValueMapping[b2Key];
                                                const items = keyMapping.key ? criteria[keyMapping.key as keyof AllCriteria]
                                                    : keyMapping?.bool
                                                        ? [true, false]
                                                        : []
                                                return (
                                                    <FormItem>
                                                        <FormLabel className="text-base font-semibold text-text-1">{t(`criteria.${keyMapping.label}`)}</FormLabel>
                                                        <FormControl>
                                                            <RadioGroup
                                                                onValueChange={field.onChange}
                                                                value={field.value}
                                                                className="flex flex-col space-y-2"
                                                            >
                                                                {items.map((item: any, index: number) => (
                                                                    <FormItem key={index}
                                                                              className="flex items-center space-x-3 space-y-0">
                                                                        <FormControl>
                                                                            <RadioGroupItem
                                                                                value={typeof item === 'boolean' ? item.toString() : item.id.toString()}/>
                                                                        </FormControl>
                                                                        <FormLabel className="font-normal text-text-1 cursor-pointer">
                                                                            {typeof item === 'boolean' ? (item ? t("common.yes") : t("common.no")) : item[getLocalizedFieldName("name")]}
                                                                        </FormLabel>
                                                                    </FormItem>
                                                                ))}
                                                            </RadioGroup>
                                                        </FormControl>
                                                        <FormMessage/>
                                                    </FormItem>
                                                )
                                            }}
                                        />
                                    )}
                                </div>

                                <div className="rounded-xl border border-border bg-bg-card p-6 space-y-4">
                                    <FormField
                                        control={form.control}
                                        name="b3Key"
                                        render={({field}) => (
                                            <FormItem>
                                                <FormLabel className="text-base font-semibold text-text-1">{t("preference.matchCriteriaX", {idx: "3"})}</FormLabel>
                                                <Select
                                                    onValueChange={value => {
                                                        field.onChange(value);
                                                        // @ts-ignore
                                                        form.setValue("b3Value", null);
                                                    }}
                                                    value={field.value}
                                                >
                                                    <FormControl>
                                                        <SelectTrigger className="h-10 bg-bg-card border-border">
                                                            <SelectValue
                                                                placeholder={t("preference.matchCriteriaPlaceholder")}/>
                                                        </SelectTrigger>
                                                    </FormControl>
                                                    <SelectContent>
                                                        {Object.entries(keyValueMapping)
                                                            .filter(([, value]) => value.broad)
                                                            .map(([key, value]) => {
                                                                const isAlreadySelected = b1Key === key || b2Key === key;
                                                                return (
                                                                    <SelectItem
                                                                        key={`${key}-${value.key}`}
                                                                        value={key}
                                                                        disabled={isAlreadySelected}
                                                                        className={isAlreadySelected ? "text-text-3" : ""}
                                                                    >
                                                                        {t(`criteria.${value.label}`)}
                                                                    </SelectItem>
                                                                );
                                                            })}
                                                    </SelectContent>
                                                </Select>
                                                <FormDescription className="text-text-3">
                                                    {t("preference.broadXDesc")}
                                                </FormDescription>
                                                <FormMessage/>
                                            </FormItem>
                                        )}
                                    />

                                    {criteria && b3Key && (
                                        <FormField
                                            control={form.control}
                                            name="b3Value"
                                            render={({field}) => {
                                                const keyMapping = keyValueMapping[b3Key];
                                                const items = keyMapping.key ? criteria[keyMapping.key as keyof AllCriteria]
                                                    : keyMapping?.bool
                                                        ? [true, false]
                                                        : []
                                                return (
                                                    <FormItem>
                                                        <FormLabel className="text-base font-semibold text-text-1">{t(`criteria.${keyMapping.label}`)}</FormLabel>
                                                        <FormControl>
                                                            <RadioGroup
                                                                onValueChange={field.onChange}
                                                                value={field.value}
                                                                className="flex flex-col space-y-2"
                                                            >
                                                                {items.map((item: any, index: number) => (
                                                                    <FormItem key={index}
                                                                              className="flex items-center space-x-3 space-y-0">
                                                                        <FormControl>
                                                                            <RadioGroupItem
                                                                                value={typeof item === 'boolean' ? item.toString() : item.id.toString()}/>
                                                                        </FormControl>
                                                                        <FormLabel className="font-normal text-text-1 cursor-pointer">
                                                                            {typeof item === 'boolean' ? (item ? t("common.yes") : t("common.no")) : item[getLocalizedFieldName("name")]}
                                                                        </FormLabel>
                                                                    </FormItem>
                                                                ))}
                                                            </RadioGroup>
                                                        </FormControl>
                                                        <FormMessage/>
                                                    </FormItem>
                                                )
                                            }}
                                        />
                                    )}
                                </div>

                                {criteria && b1Value && b2Value && b3Value && narrows.length > 0 && (

                                    <>
                                        <Separator className="bg-border"/>
                                        <div className="space-y-2">
                                            <h4 className="text-xl font-semibold text-text-1">{t("common.stepX", {step: 3})}: {t("preference.beMoreSpecific")}</h4>
                                            <p className="text-sm text-text-2">
                                                <Trans i18nKey="preference.specificCriteriaDesc"
                                                       components={{bold: <b className="font-semibold text-text-1"/>}}/>
                                            </p>
                                        </div>

                                        <div className="rounded-xl border border-border bg-bg-card p-6 space-y-4">
                                            <input type="hidden" {...form.register('s1Key')} />

                                            <FormField
                                                control={form.control}
                                                name="s1Value"
                                                render={({field}) => {
                                                    const keyMapping = keyValueMapping[narrows[0]];
                                                    const items = criteria[keyMapping.key as keyof AllCriteria];
                                                    return (
                                                        <FormItem>
                                                            <FormLabel className="text-base font-semibold text-text-1">{t(`criteria.${keyMapping.label}`)} ({t("common.optional")})</FormLabel>
                                                            <FormControl>
                                                                <RadioGroup
                                                                    onValueChange={value => field.onChange(parseInt(value))}
                                                                    value={field.value ? field.value.toString() : undefined}
                                                                    className="flex flex-col space-y-2"
                                                                >
                                                                    {items.map((item: any, index: number) => (
                                                                        <FormItem key={index}
                                                                                  className="flex items-center space-x-3 space-y-0">
                                                                            <FormControl>
                                                                                <RadioGroupItem value={item.id.toString()}/>
                                                                            </FormControl>
                                                                            <FormLabel className="font-normal text-text-1 cursor-pointer">
                                                                                {item[getLocalizedFieldName("name")]}
                                                                            </FormLabel>
                                                                        </FormItem>
                                                                    ))}
                                                                </RadioGroup>
                                                            </FormControl>
                                                            <FormMessage/>
                                                        </FormItem>
                                                    )
                                                }}
                                            />
                                        </div>
                                    </>
                                )}

                                {criteria && b1Value && b2Value && b3Value && narrows.length > 1 && (
                                    <div className="rounded-xl border border-border bg-bg-card p-6 space-y-4">
                                        <input type="hidden" {...form.register('s2Key')} />

                                        <FormField
                                            control={form.control}
                                            name="s2Value"
                                            render={({field}) => {
                                                const keyMapping = keyValueMapping[narrows[1]];
                                                const items = criteria[keyMapping.key as keyof AllCriteria];
                                                return (
                                                    <FormItem>
                                                        <FormLabel className="text-base font-semibold text-text-1">{t(`criteria.${keyMapping.label}`)} ({t("common.optional")})</FormLabel>
                                                        <FormControl>
                                                            <RadioGroup
                                                                onValueChange={value => field.onChange(parseInt(value))}
                                                                value={field.value ? field.value.toString() : undefined}
                                                                className="flex flex-col space-y-2"
                                                            >
                                                                {items.map((item: any, index: number) => (
                                                                    <FormItem key={index}
                                                                              className="flex items-center space-x-3 space-y-0">
                                                                        <FormControl>
                                                                            <RadioGroupItem value={item.id.toString()}/>
                                                                        </FormControl>
                                                                        <FormLabel className="font-normal text-text-1 cursor-pointer">
                                                                            {item[getLocalizedFieldName("name")]}
                                                                        </FormLabel>
                                                                    </FormItem>
                                                                ))}
                                                            </RadioGroup>
                                                        </FormControl>
                                                        <FormMessage/>
                                                    </FormItem>
                                                )
                                            }}
                                        />
                                    </div>
                                )}

                                {criteria && b1Value && b2Value && b3Value && narrows.length > 2 && (
                                    <div className="rounded-xl border border-border bg-bg-card p-6 space-y-4">
                                        <input type="hidden" {...form.register('s3Key')} />

                                        <FormField
                                            control={form.control}
                                            name="s3Value"
                                            render={({field}) => {
                                                const keyMapping = keyValueMapping[narrows[2]];
                                                const items = criteria[keyMapping.key as keyof AllCriteria];
                                                return (
                                                    <FormItem>
                                                        <FormLabel className="text-base font-semibold text-text-1">{t(`criteria.${keyMapping.label}`)} ({t("common.optional")})</FormLabel>
                                                        <FormControl>
                                                            <RadioGroup
                                                                onValueChange={value => field.onChange(parseInt(value))}
                                                                value={field.value ? field.value.toString() : undefined}
                                                                className="flex flex-col space-y-2"
                                                            >
                                                                {items.map((item: any, index: number) => (
                                                                    <FormItem key={index}
                                                                              className="flex items-center space-x-3 space-y-0">
                                                                        <FormControl>
                                                                            <RadioGroupItem value={item.id.toString()}/>
                                                                        </FormControl>
                                                                        <FormLabel className="font-normal text-text-1 cursor-pointer">
                                                                            {item[getLocalizedFieldName("name")]}
                                                                        </FormLabel>
                                                                    </FormItem>
                                                                ))}
                                                            </RadioGroup>
                                                        </FormControl>
                                                        <FormMessage/>
                                                    </FormItem>
                                                )
                                            }}
                                        />
                                    </div>
                                )}

                                {criteria && b1Value && b2Value && b3Value && narrows.length > 3 && (
                                    <div className="rounded-xl border border-border bg-bg-card p-6 space-y-4">
                                        <input type="hidden" {...form.register('s4Key')} />

                                        <FormField
                                            control={form.control}
                                            name="s4Value"
                                            render={({field}) => {
                                                const keyMapping = keyValueMapping[narrows[3]];
                                                const items = criteria[keyMapping.key as keyof AllCriteria];
                                                return (
                                                    <FormItem>
                                                        <FormLabel className="text-base font-semibold text-text-1">{t(`criteria.${keyMapping.label}`)} ({t("common.optional")})</FormLabel>
                                                        <FormControl>
                                                            <RadioGroup
                                                                onValueChange={value => field.onChange(parseInt(value))}
                                                                value={field.value ? field.value.toString() : undefined}
                                                                className="flex flex-col space-y-2"
                                                            >
                                                                {items.map((item: any, index: number) => (
                                                                    <FormItem key={index}
                                                                              className="flex items-center space-x-3 space-y-0">
                                                                        <FormControl>
                                                                            <RadioGroupItem value={item.id.toString()}/>
                                                                        </FormControl>
                                                                        <FormLabel className="font-normal text-text-1 cursor-pointer">
                                                                            {item[getLocalizedFieldName("name")]}
                                                                        </FormLabel>
                                                                    </FormItem>
                                                                ))}
                                                            </RadioGroup>
                                                        </FormControl>
                                                        <FormMessage/>
                                                    </FormItem>
                                                )
                                            }}
                                        />
                                    </div>
                                )}

                                <Separator className="bg-border"/>
                                <div className="space-y-2">
                                    <h4 className="text-xl font-semibold text-text-1">
                                        <strong>NEW</strong>: {t("affinity.questionTitle")}
                                    </h4>
                                    <p className="text-sm text-text-2">
                                        {t("affinity.questionDesc")}
                                    </p>
                                </div>

                                <div className="rounded-xl border border-border bg-bg-card p-6 space-y-4">
                                    <FormField
                                        control={form.control}
                                        name="wantsAffinityGroup"
                                        render={({field}) => (
                                            <FormItem>
                                                <FormControl>
                                                    <RadioGroup
                                                        onValueChange={(value) => field.onChange(value === "yes")}
                                                        value={field.value ? "yes" : "no"}
                                                        className="flex flex-col space-y-2"
                                                    >
                                                        <FormItem className="flex items-center space-x-3 space-y-0">
                                                            <FormControl>
                                                                <RadioGroupItem value="yes"/>
                                                            </FormControl>
                                                            <FormLabel className="font-normal text-text-1 cursor-pointer">{t("common.yes")}</FormLabel>
                                                        </FormItem>
                                                        <FormItem className="flex items-center space-x-3 space-y-0">
                                                            <FormControl>
                                                                <RadioGroupItem value="no"/>
                                                            </FormControl>
                                                            <FormLabel className="font-normal text-text-1 cursor-pointer">{t("common.no")}</FormLabel>
                                                        </FormItem>
                                                    </RadioGroup>
                                                </FormControl>
                                                <FormMessage/>
                                            </FormItem>
                                        )}
                                    />

                                    {wantsAffinityGroup && (
                                        <FormField
                                            control={form.control}
                                            name="affinityGroupIds"
                                            render={() => (
                                                <FormItem>
                                                    <div className="mb-4 space-y-1">
                                                        <FormLabel className="text-base font-semibold text-text-1">{t("affinity.group")}</FormLabel>
                                                        <FormDescription className="text-text-3">{t("affinity.selectGroups")}</FormDescription>
                                                    </div>
                                                    <div className="space-y-3">
                                                        {affinityGroups.map((group) => (
                                                            <FormField
                                                                key={group.id}
                                                                control={form.control}
                                                                name="affinityGroupIds"
                                                                render={({field}) => {
                                                                    const current = field.value ?? [];
                                                                    const atMax = current.length >= 3;
                                                                    const isChecked = current.includes(group.id);
                                                                    return (
                                                                        <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                                                                            <FormControl>
                                                                                <Checkbox
                                                                                    checked={isChecked}
                                                                                    disabled={!isChecked && atMax}
                                                                                    onCheckedChange={(checked) => {
                                                                                        if (checked) {
                                                                                            if (current.length < 3) field.onChange([...current, group.id]);
                                                                                        } else {
                                                                                            field.onChange(current.filter((id: number) => id !== group.id));
                                                                                        }
                                                                                    }}
                                                                                />
                                                                            </FormControl>
                                                                            <FormLabel className="font-normal text-text-1 cursor-pointer">
                                                                                {group[getLocalizedFieldName("name") as "name" | "nameFr"]}
                                                                            </FormLabel>
                                                                        </FormItem>
                                                                    );
                                                                }}
                                                            />
                                                        ))}
                                                    </div>
                                                    <FormMessage/>
                                                </FormItem>
                                            )}
                                        />
                                    )}
                                </div>
                                <div className="flex gap-3">
                                    <Button
                                        type="submit"
                                        disabled={!form.formState.isDirty || (preference ? createMutation.isPending : updateMutation.isPending)}
                                        className="bg-text-1 text-bg-card hover:opacity-95 rounded-md px-4 py-2 h-10"
                                    >
                                        {(preference ? createMutation.isPending : updateMutation.isPending) &&
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin"/>}
                                        {preference ? t("preference.update") : t("preference.submit")}
                                    </Button>

                                    <Button 
                                        type="button" 
                                        variant={"destructive"} 
                                        className="rounded-md px-4 py-2 h-10"
                                        onClick={handleReset}
                                    >
                                        {t("preference.clear")}
                                    </Button>
                                </div>
                        </form>
                    </Form>
                </div>
            </>
        )
    } else {
        return preference ? renderPreferenceDetails(preference) : (
            <div className="container max-w-4xl pb-12 pt-8">
                <div className="rounded-xl border border-border bg-bg-card p-8 text-center">
                    <h2 className="text-3xl font-bold text-text-1">
                        {t("preference.tooBadMissedIt")}
                    </h2>
                    <hr className="w-full my-8 border-border"/>
                </div>
            </div>
        )
    }
}

export default PreferenceForm;
